import React from "react";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { BUSINESS } from "../lib/constants";

export default function Contact() {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 border-t border-[#332C27] bg-[#14110F]"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-6 reveal">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#F5A623] font-bold mb-4">
              / 07 — Get in touch
            </div>
            <h2
              data-testid="contact-heading"
              className="font-display text-4xl md:text-5xl lg:text-6xl text-[#F9FAFB] font-semibold uppercase tracking-tight leading-[1.05]"
            >
              Real people.
              <br />
              <span className="text-[#F5A623]">Real phones.</span>
            </h2>
            <p className="mt-8 text-[#A19D98] text-base md:text-lg leading-relaxed max-w-lg">
              Pick up the phone, shoot us an email, or just fill out the quote
              form. Whichever way you reach out, you'll get a friendly
              human-in-a-truck on the other end.
            </p>

            <div className="mt-10 space-y-4">
              <ContactRow
                icon={<Phone className="w-5 h-5" />}
                label="Phone"
                value={BUSINESS.phone}
                href={`tel:${BUSINESS.phoneRaw}`}
                testid="contact-phone"
              />
              <ContactRow
                icon={<Mail className="w-5 h-5" />}
                label="Email"
                value={BUSINESS.email}
                href={`mailto:${BUSINESS.email}`}
                testid="contact-email"
              />
              <ContactRow
                icon={<MapPin className="w-5 h-5" />}
                label="Based in"
                value={`${BUSINESS.city}, ${BUSINESS.state} ${BUSINESS.zip}`}
                testid="contact-address"
              />
            </div>
          </div>

          <div className="lg:col-span-6 grid gap-5">
            <div
              data-testid="hours-card"
              className="reveal bg-[#1E1A18] border border-[#332C27] rounded-2xl p-8"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg bg-[#F5A623]/10 border border-[#F5A623]/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[#F5A623]" />
                </div>
                <h3 className="font-display text-xl text-[#F9FAFB] font-semibold tracking-tight">
                  Hours
                </h3>
              </div>
              <ul className="divide-y divide-[#332C27]">
                {BUSINESS.hours.map((h) => (
                  <li
                    key={h.day}
                    className="flex items-center justify-between py-3"
                  >
                    <span className="text-sm uppercase tracking-wider text-[#A19D98]">
                      {h.day}
                    </span>
                    <span className="text-sm text-[#F9FAFB] font-medium">
                      {h.time}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div
              data-testid="service-area-card"
              className="reveal bg-[#1E1A18] border border-[#332C27] rounded-2xl p-8"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg bg-[#F5A623]/10 border border-[#F5A623]/20 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-[#F5A623]" />
                </div>
                <h3 className="font-display text-xl text-[#F9FAFB] font-semibold tracking-tight">
                  Service area
                </h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {BUSINESS.serviceArea.map((a) => (
                  <span
                    key={a}
                    className="text-xs uppercase tracking-[0.15em] px-3 py-1.5 rounded-full border border-[#332C27] text-[#F9FAFB] bg-[#14110F]/60 hover:border-[#F5A623]/50 transition-colors"
                  >
                    {a}
                  </span>
                ))}
              </div>
              <p className="text-xs text-[#A19D98] mt-5 leading-relaxed">
                We proudly serve Lehigh Acres, Fort Myers and Cape Coral.
                Right on the edge? Give us a call — we'll tell you straight
                if we can make it.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon, label, value, href, testid }) {
  const inner = (
    <div className="flex items-center gap-4 group">
      <div className="w-11 h-11 rounded-xl bg-[#1E1A18] border border-[#332C27] flex items-center justify-center text-[#F5A623] group-hover:border-[#F5A623]/60 transition-colors">
        {icon}
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#A19D98] font-bold">
          {label}
        </div>
        <div className="text-lg md:text-xl text-[#F9FAFB] font-medium group-hover:text-[#F5A623] transition-colors">
          {value}
        </div>
      </div>
    </div>
  );
  return href ? (
    <a href={href} data-testid={testid} className="block">
      {inner}
    </a>
  ) : (
    <div data-testid={testid}>{inner}</div>
  );
}
